import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroll-component';

import LoadMoreButton from 'components/consumer/LoadMoreButton';
import VisuallyHidden from 'components/consumer/VisuallyHidden';
import getFirstFocusableElement from 'hooks/helpers/getFirstFocusableElement';
import MixedGridCardPlaceholder from './components/MixedGridCardPlaceholder';
import MixedGridItems from './components/MixedGridItems';
import MixedGridItemsWithReviews from './components/MixedGridItemsWithReviews';

const MixedGrid = ({
  allData,
  gridWithOneCol,
  gridWithReviews,
  hits,
  hoveredValue,
  keyId,
  layout,
  loading,
  loadingMore,
  loadMore,
  isMixedGridTab,
  maxCardsCount = 6,
  onHoverHandler,
  toggleModalView,
  allStaticProductData = [],
}) => {
  const itemContainerRef = useRef();

  return (
    <div data-xc="mixed-grid" className="mixed-grid" key={keyId}>
      <div className="mx-auto my-0" ref={itemContainerRef}>
        {loading ? (
          <div
            className={classNames(
              'grid gap-[10px] max-[767px]:mt-2 md:gap-4 min-[1200px]:grid-cols-[repeat(auto-fit,minmax(300px,33.333%))] max-[767px]:[&>*]:max-w-[100vw]',
              {
                'xl:grid-cols-[repeat(auto-fit,minmax(45%,1fr))] xl:m-0': isMixedGridTab,
              }
            )}
          >
            <MixedGridCardPlaceholder maxCardsCount={maxCardsCount} />
          </div>
        ) : (
          <>
            <InfiniteScroll
              dataLength={allData && allData.length}
              next={loadMore}
              hasMore={!!loadMore}
            >
              {gridWithReviews ? (
                <MixedGridItemsWithReviews
                  loadingMore={loadingMore}
                  data={allData}
                  hits={hits}
                  hoveredValue={hoveredValue}
                  layout={layout}
                  onHoverHandler={onHoverHandler}
                  toggleModalView={toggleModalView}
                  isMixedGridTab={isMixedGridTab}
                  allStaticProductData={allStaticProductData}
                />
              ) : gridWithOneCol ? (
                <div className="flex flex-col">
                  <MixedGridItems
                    from="review"
                    loadingMore={loadingMore}
                    data={allData}
                    hits={hits}
                    hoveredValue={hoveredValue}
                    layout={layout}
                    onHoverHandler={onHoverHandler}
                    toggleModalView={toggleModalView}
                  />
                </div>
              ) : (
                <div
                  className={classNames(
                    'grid gap-[10px] max-[767px]:mt-2 md:gap-4 min-[1200px]:grid-cols-[repeat(auto-fit,minmax(300px,33.333%))]',
                    {
                      'xl:grid-cols-[repeat(auto-fit,minmax(45%,1fr))] xl:m-0': isMixedGridTab,
                    }
                  )}
                >
                  <MixedGridItems
                    loadingMore={loadingMore}
                    data={allData}
                    hits={hits}
                    hoveredValue={hoveredValue}
                    layout={layout}
                    onHoverHandler={onHoverHandler}
                    toggleModalView={toggleModalView}
                  />
                </div>
              )}
            </InfiniteScroll>

            {!loadingMore && loadMore && (
              <>
                <VisuallyHidden>
                  On activating the Load more items button, the content above
                  will be updated
                </VisuallyHidden>

                <LoadMoreButton
                  onClick={() => {
                    loadMore();

                    const firstFocusableElement = getFirstFocusableElement(
                      itemContainerRef.current
                    );

                    if (firstFocusableElement) {
                      firstFocusableElement.focus();
                    }
                  }}
                  aria-label="Load more items"
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

MixedGrid.propTypes = {
  allData: PropTypes.array,
  allStaticProductData: PropTypes.array,
  gridWithOneCol: PropTypes.bool,
  gridWithReviews: PropTypes.number,
  hits: PropTypes.number,
  hoveredValue: PropTypes.object,
  isMixedGridTab: PropTypes.bool,
  keyId: PropTypes.string,
  layout: PropTypes.string,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  loadMore: PropTypes.func,
  maxCardsCount: PropTypes.number,
  onHoverHandler: PropTypes.func,
  toggleModalView: PropTypes.func,
};

export default MixedGrid;
